import React from 'react';
import { Icon, IconType } from '@chargify/icons';

import { getInputStatus } from './utils';
import { Container, IconContainer, StyledInput } from './Input.styles';

import { ComponentSize } from '../types';
import { Variant } from './types';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Component value
   */
  value?: string;
  /**
   * Component size
   */
  inputSize?: ComponentSize;
  /**
   * Component left icon
   */
  iconLeft?: IconType;
  /**
   * Component right icon
   */
  iconRight?: IconType;
  /**
   * Component disabled state
   */
  disabled?: boolean;
  /**
   * Component error state
   */
  hasError?: boolean;
  /**
   * Component variant
   */
  variant?: Variant;
  /**
   * Input full width indicator
   */
  fullWidth?: boolean;
  /**
   * Onchange handler
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

/**
- The InputLabel component allows to easily create labels for a labelable form-related element 
**/
const Input = ({
  value,
  inputSize = 'medium',
  disabled = false,
  iconLeft,
  iconRight,
  hasError = false,
  variant = 'primary',
  fullWidth = false,
  onChange,
  ...props
}: InputProps) => (
  <Container>
    {iconLeft && (
      <IconContainer
        iconSize={inputSize}
        hasIconLeft
        data-testid="input-left-icon"
      >
        <Icon fill="currentColor" type={iconLeft} />
      </IconContainer>
    )}
    <StyledInput
      onChange={onChange}
      inputSize={inputSize}
      value={value}
      hasIconLeft={!!iconLeft}
      hasIconRight={!!iconRight}
      variant={variant}
      status={getInputStatus(disabled, hasError)}
      disabled={disabled}
      fullWidth={fullWidth}
      {...props}
    />
    {iconRight && (
      <IconContainer
        iconSize={inputSize}
        hasIconRight
        data-testid="input-right-icon"
      >
        <Icon fill="currentColor" type={iconRight} />
      </IconContainer>
    )}
  </Container>
);

export default Input;
